import { useEffect, useMemo, useState } from "react";
import { useApi } from "./useApi";
import { fetchBrands, buildProductQuery, buildAiSearchQuery } from "../services/exploreService";
import { normalizeCategories } from "../utils/categoryHelpers";

const DEFAULT_LIMIT = 12;

export function useExploreProducts() {
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [saleOnly, setSaleOnly] = useState(false);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [aiQuery, setAiQuery] = useState("");

  const [brands, setBrands] = useState([]);
  const { data: categoriesData } = useApi("/categories");
  const categories = useMemo(() => normalizeCategories(categoriesData), [categoriesData]);

  useEffect(() => {
    fetchBrands().then(setBrands);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [category, brand, search, sort, priceMin, priceMax, inStockOnly, saleOnly, featuredOnly, aiQuery]);

  const productsUrl = aiQuery
    ? null
    : "/products?" + buildProductQuery({ category, brand, search, sort, featuredOnly, page, limit: DEFAULT_LIMIT });
  const { data: productsData, loading: productsLoading } = useApi(productsUrl, [productsUrl]);

  const aiUrl = aiQuery
    ? "/products/ai-search?" + buildAiSearchQuery({ q: aiQuery, category, brand, limit: 48 })
    : null;
  const { data: aiData, loading: aiLoading } = useApi(aiUrl, [aiUrl]);

  const { data: statsData } = useApi(
    "/products?" + buildProductQuery({ page: 1, limit: 1 }),
    [],
  );

  const isAiMode = Boolean(aiQuery);
  const rawList = isAiMode ? aiData?.data : productsData?.data;

  const filteredList = useMemo(() => {
    let list = Array.isArray(rawList) ? [...rawList] : [];
    if (priceMin !== "") {
      const min = parseFloat(priceMin);
      list = list.filter((p) => parseFloat(p.sale_price ?? p.price) >= min);
    }
    if (priceMax !== "") {
      const max = parseFloat(priceMax);
      list = list.filter((p) => parseFloat(p.sale_price ?? p.price) <= max);
    }
    if (inStockOnly) list = list.filter((p) => Number(p.stock) > 0);
    if (saleOnly) list = list.filter((p) => p.sale_price && Number(p.sale_price) < Number(p.price));
    if (isAiMode && featuredOnly) list = list.filter((p) => p.is_featured);
    if (sort === "featured") {
      list.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0) || a.name.localeCompare(b.name));
    }
    return list;
  }, [rawList, priceMin, priceMax, inStockOnly, saleOnly, featuredOnly, isAiMode, sort]);

  const aiPageCount = Math.ceil(filteredList.length / DEFAULT_LIMIT) || 1;
  const aiPageItems = isAiMode
    ? filteredList.slice((page - 1) * DEFAULT_LIMIT, page * DEFAULT_LIMIT)
    : filteredList;

  const total = isAiMode ? filteredList.length : productsData?.total ?? 0;
  const totalPages = isAiMode ? aiPageCount : Math.max(1, Math.ceil((productsData?.total ?? 0) / DEFAULT_LIMIT));

  const runAiSearch = (query) => {
    const trimmed = (query || "").trim();
    if (trimmed.length < 2) return false;
    setAiQuery(trimmed);
    return true;
  };

  const clearAiSearch = () => setAiQuery("");

  const resetFilters = () => {
    setCategory("");
    setBrand("");
    setSearch("");
    setSort("newest");
    setPriceMin("");
    setPriceMax("");
    setInStockOnly(false);
    setSaleOnly(false);
    setFeaturedOnly(false);
    setAiQuery("");
  };

  return {
    categories,
    brands,
    products: aiPageItems,
    total,
    page,
    totalPages,
    loading: isAiMode ? aiLoading : productsLoading,
    isAiMode,
    aiQuery,
    stats: {
      totalProducts: statsData?.total ?? 0,
      totalBrands: brands.length,
      totalCategories: categories.length,
    },
    filters: {
      category,
      brand,
      search,
      sort,
      priceMin,
      priceMax,
      inStockOnly,
      saleOnly,
      featuredOnly,
    },
    setCategory: (v) => {
      clearAiSearch();
      setCategory(v);
    },
    setBrand: (v) => {
      clearAiSearch();
      setBrand(v);
    },
    setSearch: (v) => {
      clearAiSearch();
      setSearch(v);
    },
    setSort,
    setPage,
    setPriceMin,
    setPriceMax,
    setInStockOnly,
    setSaleOnly,
    setFeaturedOnly,
    runAiSearch,
    clearAiSearch,
    resetFilters,
  };
}
